import { useMemo } from 'react';
import type * as CSS from 'csstype';
import { observer } from 'mobx-react-lite';
import { AspectRatio } from 'react-aspect-ratio';
import { ParticipantPresenterProps } from 'features/ParticipantItem/types';
import { useParticipant } from 'core';
import { BASE_ASPECT_RATIO } from 'consts';
import { ReactionIcon } from 'features/Reactions';
import { selectParticipantReaction } from 'state/selectors';
import { isMainModeratorParticipant, isModeratorParticipant } from 'useCase';
import { isParticipantSpeaker } from 'useCase/webinar/webinarStatus';
import { OrientationEnum, useParticipantItem } from '../../hooks/useParticipantItem';
import { ParticipantBar } from '../../components/ParticipantBar';
import { ParticipantVideo } from '../../components/ParticipantVideo';
import { ViewerPlaceholder, ViewerVideoRenderer } from './view';

interface Props extends ParticipantPresenterProps {}

const ViewerVCSComponent = ({
    isAudioMuted,
    isScreenShared,
    isVideoEnabled,
    isShowPulseAnimation,
    participant,
    onSizeChanged,
}: Props) => {
    const { cameraPublication, isLocal } = useParticipant(participant);
    const { getDisplayData, getObjectFit } = useParticipantItem();
    const objectFit: CSS.Properties['objectFit'] = getObjectFit(
        OrientationEnum.Landscape,
        cameraPublication?.dimensions,
    );
    const { currentName, placeholder } = useMemo(() => getDisplayData(participant, isLocal), [participant, isLocal]);
    const track = cameraPublication?.track;
    const isShowVideo = isVideoEnabled && track;

    const reaction = selectParticipantReaction(participant);
    const isModerator = isModeratorParticipant(participant);
    const isMainModerator = isMainModeratorParticipant(participant);
    const isSpeaker = isParticipantSpeaker(participant);

    return (
        <>
            <AspectRatio ratio={BASE_ASPECT_RATIO}>
                <ParticipantVideo
                    track={track}
                    isLocal={isLocal}
                    withSpeakerPulseAnimation={isShowPulseAnimation}
                    isShowVideo={Boolean(isShowVideo)}
                    cameraPublication={cameraPublication}
                    participant={participant}
                    objectFit={objectFit}
                    placeholder={placeholder}
                    onSizeChanged={onSizeChanged}
                    VideoRenderer={ViewerVideoRenderer}
                    Placeholder={ViewerPlaceholder}
                    snapshotFirst={true}
                />
            </AspectRatio>
            <ParticipantBar
                isAudioMuted={isAudioMuted}
                name={currentName}
                isScreenShareIconShown={isScreenShared}
                isModerator={isModerator}
                isMainModerator={isMainModerator}
                isSpeaker={isSpeaker}
            />
            {reaction && <ReactionIcon reaction={reaction} />}
        </>
    );
};

export const ViewerVCS = observer(ViewerVCSComponent);
